<template>
  <div class="welcome-container">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <div class="d-flex justify-content-between align-items-center">
              <h2 class="card-title mb-0">
                <i class="fa fa-rocket text-primary mr-2"></i>
                Welcome to CabCard!
              </h2>
              <button 
                type="button" 
                class="btn btn-sm btn-link text-muted"
                @click="dismissWelcome"
              >
                <i class="fa fa-times"></i> Dismiss
              </button>
            </div>
          </div>
          <div class="card-body">
            <p class="lead">You're ready to start accepting payments.</p>
            
            <div class="row mt-4">
              <!-- SoftPOS Card -->
              <div class="col-md-4 mb-4">
                <div class="card h-100" :class="{ 'border-success': milestones.softpos }">
                  <div class="card-body d-flex flex-column">
                    <div class="text-center mb-3">
                      <i class="fa fa-mobile fa-3x" :class="milestones.softpos ? 'text-success' : 'text-muted'"></i>
                    </div>
                    <h5 class="card-title">
                      Take a SoftPOS Payment
                      <i v-if="milestones.softpos" class="fa fa-check-circle text-success ml-2"></i>
                    </h5>
                    <p class="card-text flex-grow-1">
                      Turn any Android phone into a payment terminal with our mobile app.
                    </p>
                    
                    <div v-if="!milestones.softpos" class="mt-auto">
                      <!-- Show QR code on desktop, button on mobile -->
                      <div v-if="isDesktop" class="text-center">
                        <div class="mb-3">
                          <qrcode-vue 
                            :value="softPosUrl" 
                            :size="150"
                            level="M"
                          ></qrcode-vue>
                        </div>
                        <p class="small text-muted">Scan to download the CabCard app</p>
                      </div>
                      <a 
                        v-else
                        :href="softPosUrl" 
                        target="_blank"
                        class="btn btn-primary btn-block"
                      >
                        <i class="fa fa-download mr-2"></i>
                        Download CabCard App
                      </a>
                    </div>
                    <div v-else class="mt-auto">
                      <div class="alert alert-success mb-0">
                        <i class="fa fa-check mr-2"></i>
                        Completed!
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Payment Links Card -->
              <div class="col-md-4 mb-4">
                <div class="card h-100" :class="{ 'border-success': milestones.paymentLink }">
                  <div class="card-body d-flex flex-column">
                    <div class="text-center mb-3">
                      <i class="fa fa-paper-plane fa-3x" :class="milestones.paymentLink ? 'text-success' : 'text-muted'"></i>
                    </div>
                    <h5 class="card-title">
                      Send a Payment Link
                      <i v-if="milestones.paymentLink" class="fa fa-check-circle text-success ml-2"></i>
                    </h5>
                    <p class="card-text flex-grow-1">
                      Request payments remotely by sending secure payment links to your customers.
                    </p>
                    
                    <div v-if="!milestones.paymentLink" class="mt-auto">
                      <router-link 
                        to="/payment-links/create" 
                        class="btn btn-primary btn-block"
                      >
                        <i class="fa fa-plus mr-2"></i>
                        Create Payment Link
                      </router-link>
                    </div>
                    <div v-else class="mt-auto">
                      <div class="alert alert-success mb-0">
                        <i class="fa fa-check mr-2"></i>
                        Completed!
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- POS Terminal Card -->
              <div class="col-md-4 mb-4">
                <div class="card h-100" :class="{ 'border-success': milestones.pos }">
                  <div class="card-body d-flex flex-column">
                    <div class="text-center mb-3">
                      <i class="fa fa-credit-card fa-3x" :class="milestones.pos ? 'text-success' : 'text-muted'"></i>
                    </div>
                    <h5 class="card-title">
                      Take a POS Payment
                      <i v-if="milestones.pos" class="fa fa-check-circle text-success ml-2"></i>
                    </h5>
                    <p class="card-text flex-grow-1">
                      Our pocket-sized 4G terminal is reliable and easy to use. 
                    </p>
                    
                    <div v-if="!milestones.pos" class="mt-auto">
                      <a 
                        v-if="!hasTerminal"
                        :href="posPurchaseUrl" 
                        target="_blank"
                        class="btn btn-primary btn-block"
                      >
                        <i class="fa fa-shopping-cart mr-2"></i>
                        Buy a Terminal
                      </a>
                      <a 
                        v-else
                        :href="posSupportUrl" 
                        target="_blank"
                        class="btn btn-primary btn-block"
                      >
                        <i class="fa fa-calculator mr-2"></i>
                        Try your Terminal
                      </a>
                    </div>
                    <div v-else class="mt-auto">
                      <div class="alert alert-success mb-0">
                        <i class="fa fa-check mr-2"></i>
                        Completed!
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Progress Bar -->
            <div class="mt-4">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h6 class="mb-0">Your Progress</h6>
                <span class="text-muted">{{ completedCount }}/3 completed</span>
              </div>
              <div class="progress" style="height: 25px;">
                <div 
                  class="progress-bar bg-success" 
                  role="progressbar" 
                  :style="`width: ${progressPercentage}%`"
                  :aria-valuenow="progressPercentage" 
                  aria-valuemin="0" 
                  aria-valuemax="100"
                >
                  {{ progressPercentage }}%
                </div>
              </div>
            </div>

            <!-- Completion Message -->
            <div v-if="allCompleted" class="mt-4">
              <div class="alert alert-success">
                <h5 class="alert-heading">
                  <i class="fa fa-trophy mr-2"></i>
                  Congratulations!
                </h5>
                <p class="mb-0">
                  You've completed all the getting started steps. You're now ready to make the most of your CabCard account!
                </p>
                <button 
                  type="button" 
                  class="btn btn-default btn-sm mt-3"
                  @click="dismissWelcome"
                >
                  Continue to Dashboard
                  <i class="fa fa-arrow-right ml-2"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QrcodeVue from 'qrcode.vue';

export default {
  name: 'WelcomeComponent',
  components: {
    QrcodeVue
  },
  data() {
    return {
      milestones: {
        softpos: false,
        paymentLink: false,
        pos: false
      },
      softPosUrl: 'https://cab.link/cc-go',
      posSupportUrl: 'https://support.cabcard.services/en_GB/terminals',
      loading: true,
      checkInterval: null,
      hasTerminal: false
    };
  },
  computed: {
    submerchant() {
      return this.$store.state.submerchant;
    },
    user() {
      return this.$store.state.user;
    },
    posPurchaseUrl() {
      const url = new URL('https://forms.cabcard.services/order-pocket-terminal')

      // prefill the purchase form by setting the query params
      url.searchParams.set('submerchant_id', this.submerchant.id);
      url.searchParams.set('user_id', this.user.id);
      url.searchParams.set('submerchant_name', this.submerchant.name);
      url.searchParams.set('submerchant_email', this.submerchant.emailAddress);
      url.searchParams.set('address_line_1', this.submerchant.addressLine1);
      url.searchParams.set('address_line_2', this.submerchant.addressLine2 || '');
      url.searchParams.set('address_city', this.submerchant.addressCity);
      url.searchParams.set('address_postcode', this.submerchant.postcode);

      return url;
    },
    isDesktop() {
      return window.innerWidth >= 768;
    },
    completedCount() {
      return Object.values(this.milestones).filter(v => v).length;
    },
    progressPercentage() {
      return Math.round((this.completedCount / 3) * 100);
    },
    allCompleted() {
      return this.completedCount === 3;
    }
  },
  methods: {
    checkTerminals() {
      this.axios
        .get(
          `v1/terminals?currentAllocation[submerchantId]=${this.submerchant.id}`,
          {
            params: {
              limit: 1, // only load 1
            },
          }
        )
        .then((response) => {
          if (response.status !== 200) {
            this.error = response.statusText;
            return;
          }

          if (response.data && response.data.length > 0) {
            this.hasTerminal = true;
          } else {
            this.hasTerminal = false;
          }
        })
        .catch((error) => {
          // Request failed.
          console.error('Error checking terminals:', error);
        });
    },
    checkMilestones() {
      // Check if they have a terminal
      this.checkTerminals();
      
      // Check SoftPOS milestone
      this.checkPaymentMethod('softpos', 'softpos');
      
      // Check Payment Link milestone
      this.checkPaymentMethod('ecommerce', 'paymentLink');
      
      // Check POS milestone
      this.checkPaymentMethod('pos', 'pos');
    },
    checkPaymentMethod(method, milestone) {
      this.axios
        .get('/v1/sales', {
          params: {
            // submerchantId: this.submerchant.id,
            method: method,
            fields: 'id',
            limit: 1
          }
        })
        .then(response => {
          if (response.data && response.data.length > 0) {
            this.milestones[milestone] = true;
          }
        })
        .catch(error => {
          console.error(`Error checking ${method} milestone:`, error);
        });
    },
    dismissWelcome() {
      this.$swal({
        title: 'Go to dashboard?',
        // text: 'You can always access these features from the main menu.',
        icon: 'question',
        showCancelButton: true,
        reverseButtons: true,
        confirmButtonText: 'Yes, go to dashboard',
        cancelButtonText: 'No, stay here',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33'
      }).then((result) => {
        if (result.isConfirmed) {
          this.$ls.set(`welcome_dismissed_${this.submerchant.id}`, 'true');
          this.$emit('dismissed');
          
          // Show success message
          this.$swal({
            title: 'Welcome to your dashboard',
            // text: 'You can now see your dashboard.',
            icon: 'success',
            timer: 1000,
            showConfirmButton: false
          });
        }
      });
    },
    startPeriodicCheck() {
      // Check milestones every minute (60000ms)
      this.checkInterval = setInterval(() => {
        this.checkMilestones();
      }, 60 * 1000);
    }
  },
  mounted() {
    this.checkMilestones();
    this.startPeriodicCheck();
  },
  beforeDestroy() {
    // Clear the interval when component is destroyed
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  }
};
</script>

<style scoped>
.welcome-container {
  animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.card {
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

.progress {
  background-color: #e9ecef;
}

.progress-bar {
  transition: width 0.6s ease;
}
</style>
